// Aba "Credenciais" do white label: as credenciais de integração que o parceiro usa para pegar
// token (mesma tabela lida por edge e partner).
//
// O clientSecret NUNCA vem na listagem — ele é buscado sob demanda, por credencial, numa rota
// dedicada e auditada no gateway. A chave privada não é exposta em lugar nenhum.
import {
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  ShieldAlertIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "@/hooks/use-toast"
import {
  useAppCredentials,
  useCreateAppCredentialMutation,
  useDeleteAppCredentialMutation,
  useRevealAppCredentialSecretMutation,
  useUpdateAppCredentialMutation,
} from "@/services/admin"
import type { AppCredential } from "@/types/app-credential"
import { DateHelper } from "@/utils"

import { decodePublicKey } from "../app-credential-public-key"
import {
  CreateCredentialDialog,
  EditCredentialDialog,
} from "./app-credential-dialogs"

const panelPrefix = "pages.adminWhiteLabels.appCredentials"

const SKELETON_ROWS = 3
const EM_DASH = "—"
const MASKED_SECRET = "••••••••••••••••"

type AppCredentialsTabProps = {
  uuid: string
}

export const AppCredentialsTab = ({
  uuid,
}: AppCredentialsTabProps) => {
  const { t } = useTranslation()

  const { data, isLoading, isError, refetch } =
    useAppCredentials(uuid)
  const createMutation = useCreateAppCredentialMutation()
  const updateMutation = useUpdateAppCredentialMutation()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editing, setEditing] = useState<
    AppCredential | undefined
  >()

  const credentials = data?.result ?? []

  if (isLoading) {
    return (
      <div
        data-testid="wl-app-credentials-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: SKELETON_ROWS }).map(
          (_, index) => (
            <Skeleton
              key={`wl-app-credentials-skeleton-${index}`}
              className="h-44 w-full rounded-xl"
            />
          )
        )}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border py-10">
        <TriangleAlertIcon
          size={40}
          strokeWidth={1.5}
          className="text-red-600"
        />
        <p className="text-sm text-muted-foreground">
          {t(`${panelPrefix}.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
        >
          {t("common.tryAgain")}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {t(`${panelPrefix}.description`)}
        </p>
        <Button
          type="button"
          onClick={() => setIsCreateOpen(true)}
        >
          <PlusIcon size={16} className="mr-2" />
          {t(`${panelPrefix}.create`)}
        </Button>
      </div>

      {/* Aviso de área sensível: o que está nesta aba concede acesso à API em nome do lojista. */}
      <div
        role="note"
        className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
      >
        <ShieldAlertIcon
          size={18}
          className="mt-0.5 shrink-0"
        />
        <p>{t(`${panelPrefix}.sensitiveNotice`)}</p>
      </div>

      {credentials.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-12 text-muted-foreground">
          <KeyRoundIcon size={40} strokeWidth={1.5} />
          <p>{t(`${panelPrefix}.empty`)}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {credentials.map((credential) => (
            <CredentialCard
              key={credential.uuid}
              credential={credential}
              onEdit={() => setEditing(credential)}
            />
          ))}
        </ul>
      )}

      <CreateCredentialDialog
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        isPending={createMutation.isPending}
        onSubmit={(values) =>
          createMutation.mutate(
            { ...values, whiteLabelId: uuid },
            { onSuccess: () => setIsCreateOpen(false) }
          )
        }
      />

      <EditCredentialDialog
        credential={editing}
        onOpenChange={(open) =>
          setEditing(open ? editing : undefined)
        }
        isPending={updateMutation.isPending}
        onSubmit={(values) =>
          updateMutation.mutate(
            { uuid: editing?.uuid as string, ...values },
            { onSuccess: () => setEditing(undefined) }
          )
        }
      />
    </div>
  )
}

interface CredentialCardProps {
  credential: AppCredential
  onEdit: () => void
}

/**
 * Card da credencial. O segredo tem zona visual própria (âmbar + cadeado) porque é o único dado
 * aqui que concede acesso: revelar é um clique explícito, que dispara a rota auditada, e o valor
 * não vem junto da listagem nem fica em cache.
 */
const CredentialCard = ({
  credential,
  onEdit,
}: CredentialCardProps) => {
  const { t } = useTranslation()
  const revealMutation =
    useRevealAppCredentialSecretMutation()
  const deleteMutation = useDeleteAppCredentialMutation()
  const [secret, setSecret] = useState<string | undefined>()
  const [isPublicKeyOpen, setIsPublicKeyOpen] =
    useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const handleToggleSecret = () => {
    if (secret) {
      setSecret(undefined)
      // Limpar só o state local deixaria o segredo legível no cache da mutation (visível no
      // React Query Devtools). "Ocultar" tem que apagar as duas cópias.
      revealMutation.reset()
      return
    }
    revealMutation.mutate(credential.uuid, {
      onSuccess: (data) => setSecret(data.clientSecret),
    })
  }

  /**
   * A área de transferência exige contexto seguro e permissão do usuário; sem o catch a
   * promise rejeita calada e o operador acha que copiou algo que não foi copiado.
   */
  const copyToClipboard = async (
    value: string,
    successKey: string,
    errorKey: string
  ) => {
    try {
      await navigator.clipboard.writeText(value)
      toast({
        title: t(`${panelPrefix}.${successKey}`),
        variant: "success",
      })
    } catch {
      toast({
        title: t(`${panelPrefix}.${errorKey}`),
        variant: "destructive",
      })
    }
  }

  const handleCopySecret = () => {
    if (!secret) return
    copyToClipboard(
      secret,
      "secretCopied",
      "secretCopyError"
    )
  }

  const handleCopyClientId = () =>
    copyToClipboard(
      credential.clientId,
      "clientIdCopied",
      "clientIdCopyError"
    )

  const handleCopyPublicKey = () =>
    copyToClipboard(
      decodePublicKey(credential.publicKey),
      "publicKeyCopied",
      "publicKeyCopyError"
    )

  return (
    <li className="rounded-xl border bg-card p-4 shadow-sm transition-colors hover:border-foreground/20">
      {/* Identidade da credencial — o que o operador procura primeiro. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700"
          >
            <KeyRoundIcon size={18} />
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold">
              {credential.name}
            </p>
            <div className="mt-0.5 flex items-center gap-1">
              <span className="truncate font-mono text-xs text-muted-foreground">
                {credential.clientId}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                onClick={handleCopyClientId}
                aria-label={t(
                  `${panelPrefix}.copyClientId`
                )}
              >
                <CopyIcon size={13} />
              </Button>
            </div>
          </div>
        </div>

        {/* Editar e chave pública convivem; remover fica separado por divisória. */}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsPublicKeyOpen(true)}
            disabled={!credential.publicKey}
          >
            <KeyRoundIcon size={14} className="mr-1" />
            {t(`${panelPrefix}.publicKey`)}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onEdit}
          >
            <PencilIcon size={14} className="mr-1" />
            {t("common.edit")}
          </Button>
          <span
            aria-hidden
            className="mx-1 h-5 w-px bg-border"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-red-700 hover:bg-red-50 hover:text-red-800"
            onClick={() => setIsDeleteOpen(true)}
            aria-label={t(`${panelPrefix}.deleteAction`)}
          >
            <Trash2Icon size={14} />
          </Button>
        </div>
      </div>

      {/* O segredo tem zona própria: fundo âmbar + cadeado dizem "isto não é um campo comum". */}
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <LockIcon
              size={14}
              className="shrink-0 text-amber-700"
              aria-hidden
            />
            <span className="text-xs font-medium text-amber-900">
              {t(`${panelPrefix}.table.secret`)}
            </span>
            <span className="truncate font-mono text-xs text-amber-950">
              {secret ?? MASKED_SECRET}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-amber-900 hover:bg-amber-100"
              onClick={handleToggleSecret}
              disabled={revealMutation.isPending}
              aria-label={t(
                secret
                  ? `${panelPrefix}.hideSecret`
                  : `${panelPrefix}.revealSecret`
              )}
            >
              {secret ? (
                <EyeOffIcon size={14} />
              ) : (
                <EyeIcon size={14} />
              )}
            </Button>
            {secret && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-amber-900 hover:bg-amber-100"
                onClick={handleCopySecret}
                aria-label={t(`${panelPrefix}.copySecret`)}
              >
                <CopyIcon size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Metadados abaixo: importam, mas não competem com identidade nem com o segredo. */}
      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <div className="flex gap-1.5">
          <dt>{t(`${panelPrefix}.table.expiration`)}:</dt>
          <dd className="font-medium tabular-nums text-foreground">
            {credential.expirationTokenTime
              ? t(`${panelPrefix}.seconds`, {
                  count: credential.expirationTokenTime,
                })
              : EM_DASH}
          </dd>
        </div>
        {credential.createdAt && (
          <div className="flex gap-1.5">
            <dt>{t(`${panelPrefix}.table.createdAt`)}:</dt>
            <dd className="font-medium tabular-nums text-foreground">
              {DateHelper.formatDate(
                credential.createdAt,
                "dd/MM/yyyy"
              )}
            </dd>
          </div>
        )}
      </dl>

      <PublicKeyDialog
        isOpen={isPublicKeyOpen}
        onOpenChange={setIsPublicKeyOpen}
        credential={credential}
        onCopy={handleCopyPublicKey}
      />

      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${panelPrefix}.deleteTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${panelPrefix}.deleteDescription`, {
                clientId: credential.clientId,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate(credential.uuid, {
                  onSuccess: () => setIsDeleteOpen(false),
                })
              }
            >
              {t(`${panelPrefix}.deleteConfirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

interface PublicKeyDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  credential: AppCredential
  onCopy: () => void
}

/**
 * A chave pública não é segredo — pode aparecer inteira. O que ela precisa é sair daqui em
 * formato utilizável: o gateway guarda em base64, e é o PEM que o parceiro configura.
 */
const PublicKeyDialog = ({
  isOpen,
  onOpenChange,
  credential,
  onCopy,
}: PublicKeyDialogProps) => {
  const { t } = useTranslation()
  const pem = decodePublicKey(credential.publicKey)

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(`${panelPrefix}.publicKeyTitle`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${panelPrefix}.publicKeyDescription`, {
              clientId: credential.clientId,
            })}
          </DialogDescription>
        </DialogHeader>

        <pre className="max-h-80 overflow-auto rounded-md border bg-muted p-3 text-left font-mono text-xs">
          {pem}
        </pre>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCopy}
          >
            <CopyIcon size={14} className="mr-1" />
            {t(`${panelPrefix}.copyPublicKey`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
